// Gravity Grip (skill 'grip', owner 2026-11-02 'Make them all': a telekinetic lift, hold and slam at a target spot; it must
// feel different from Singularity, which drifts and pulls). Render-only: reads sim.grips (spot, radius, timing, the foes it
// caught) and each foe's gripY (the sim's held height: the view draws the foe there), plus the 'grip' / 'gripSlam' events;
// decides nothing. Psychic magenta look:
//  - a seal flares open on the ground at the spot (two rings, a turning band of runic dashes, six spokes), brightening as
//    the slam nears
//  - every caught foe: a thin crisp tether of light from the seal up to it, a dashed pink ring turning around it, pink motes
//    streaming up under it while it rises and speed lines streaking down as it is slammed
//  - the slam: a white pinch, a pink shock ring racing out to the shock radius, dust bursting out of the crater, rock chips,
//    a dust puff where each foe lands, dark radial cracks, a hard camera kick
import * as THREE from 'three/webgpu';
import { abs, atan, float, fract, length, mix, mx_noise_float, sin, smoothstep, step, time, uv, vec2, vec3 } from 'three/tsl';
import type { Sim, SimEvent } from '../core/sim';
import { GRIP_FALL, GRIP_LIFT } from '../core/skillStats';
import type { Fx } from './fx';
import { FLASH } from './fx';
import type { GroundGlow } from './groundGlow';
import { Cards, Decals, Ribbons, clamp01, col, easeOut, haloLook, pushRolled } from './skillsBShared';

const BODY = 0xff5ac8, CORE = 0xffe6f6, GLOW = 0xff58c0, DUSTC = 0xc8b49a;
const POOL = 3, HELD = 72;
const _e = new THREE.Color(), _c = new THREE.Color();

/** the ground seal. a = (alpha, charge 0..1 toward the slam, flash, evolved) */
function sealLook(a: any): THREE.MeshBasicNodeMaterial { // eslint-disable-line @typescript-eslint/no-explicit-any
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, fog: false });
  const p = uv().sub(vec2(0.5, 0.5)), d = length(p).mul(2), ang = atan(p.y, p.x).div(Math.PI * 2);
  const lineAt = (r: number, w: number) => smoothstep(w, 0, abs(d.sub(r)));
  const outer = lineAt(0.95, 0.025), inner = lineAt(0.7, 0.018), core = lineAt(0.24, 0.02);
  const runes = step(0.42, fract(ang.mul(28).sub(time.mul(0.35)))).mul(step(0.76, d)).mul(step(d, 0.89)).mul(step(0.3, fract(ang.mul(7).add(d.mul(3)))));
  const spokes = smoothstep(0.035, 0, abs(fract(ang.mul(6).add(time.mul(0.12))).sub(0.5)).mul(d.max(0.3))).mul(step(0.26, d)).mul(step(d, 0.69));
  const fill = smoothstep(0.95, 0.1, d).mul(mix(float(0.08), float(0.3), a.y));
  const lines = outer.add(inner).add(core).add(runes.mul(0.8)).add(spokes.mul(0.7)).min(1);
  _c.setHex(BODY);
  m.colorNode = mix(vec3(_c.r, _c.g, _c.b).mul(1.25), vec3(1.6, 1.4, 1.6), lines.mul(mix(float(0.25), float(0.8), a.y)).add(a.z).min(1));
  m.opacityNode = lines.add(fill).min(1).mul(a.x);
  return m;
}
/** a camera-facing dashed ring outline (the grip on each foe). a = (alpha, heat) */
function gripRingLook(a: any): THREE.MeshBasicNodeMaterial { // eslint-disable-line @typescript-eslint/no-explicit-any
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false });
  const p = uv().sub(vec2(0.5, 0.5)), d = length(p).mul(2), ang = atan(p.y, p.x).div(Math.PI * 2);
  const band = smoothstep(0.07, 0.0, abs(d.sub(0.86))), dash = step(0.3, fract(ang.mul(10)));
  const hot = smoothstep(0.03, 0.0, abs(d.sub(0.86)));
  _c.setHex(BODY);
  m.colorNode = mix(vec3(_c.r, _c.g, _c.b).mul(1.3), vec3(1.7, 1.5, 1.7), hot.mul(a.y.mul(0.5).add(0.5)));
  m.opacityNode = band.mul(dash.mul(0.75).add(0.25)).mul(a.x);
  return m;
}
/** dark radial cracks left by the slam. a = (alpha) */
function crackLook(a: any): THREE.MeshBasicNodeMaterial { // eslint-disable-line @typescript-eslint/no-explicit-any
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, fog: false });
  const p = uv().sub(vec2(0.5, 0.5)), d = length(p).mul(2), ang = atan(p.y, p.x).div(Math.PI * 2);
  // N cracks; each wanders a little (noise) and has its own length; the line keeps a constant width (arc length, not angle)
  // and tapers to a point at its end
  const N = 13, wig = mx_noise_float(vec3(d.mul(4), ang.mul(N).floor(), 1.7)).mul(0.22);
  const sector = ang.mul(N).add(wig).floor(), len = fract(sin(sector.mul(12.9898)).mul(43758.5453)).mul(0.45).add(0.5);
  const arc = abs(fract(ang.mul(N).add(wig)).sub(0.5)).mul(d).mul(Math.PI * 2 / N); // distance to the crack's centre line
  const w = float(0.016).mul(float(1).sub(d.div(len)).max(0)).add(0.003);
  const crack = smoothstep(w, w.mul(0.4), arc).mul(step(0.1, d)).mul(step(d, len));
  m.colorNode = vec3(0.09, 0.05, 0.05);
  m.opacityNode = crack.mul(0.9).add(smoothstep(0.3, 0.0, d).mul(0.3)).min(1).mul(a.x);
  return m;
}

export class GripFx {
  private seals: Decals; private cracks: Decals; private rings: Cards; private auras: Cards; private tethers: Ribbons;
  private crk: { on: boolean; x: number; z: number; y: number; r: number; t: number; yaw: number }[] = Array.from({ length: 4 }, () => ({ on: false, x: 0, z: 0, y: 0, r: 1, t: 0, yaw: 0 }));
  private slamFlash = new Float32Array(POOL);
  private seg = new Float32Array(6);
  /** QA counters */
  casts = 0; slams = 0; lifted = 0;
  /** hero gesture: hands-forward weight toward the spot while it lifts and holds; slamSeq bumps on each slam */
  pose = 0; aimYaw = 0; slamSeq = 0;
  /** freeze-frame request for the slam (the view takes it) */
  hitstop = 0;

  constructor(scene: THREE.Scene, private fx: Fx, private glow: GroundGlow, private groundAt: (x: number, z: number) => number, private burn: (x: number, z: number, r: number) => void) {
    this.cracks = new Decals(scene, 4, crackLook, 1);
    this.seals = new Decals(scene, POOL, sealLook, 2);
    this.auras = new Cards(scene, HELD, haloLook(GLOW, 0.45), 4);
    this.rings = new Cards(scene, HELD, gripRingLook, 6);
    this.tethers = new Ribbons(scene, HELD, 2, 5);
  }

  event(ev: SimEvent, sim: Sim): void {
    const fx = this.fx;
    if (ev.t === 'grip') {
      this.casts++; this.lifted += ev.n;
      const gy = this.groundAt(ev.x, ev.z), p = sim.player;
      this.aimYaw = Math.atan2(ev.x - p.x, ev.z - p.z);
      fx.rings.add(ev.x, gy + 0.12, ev.z, ev.r * 1.2, 0.3, BODY, 2.2, 0.6);
      fx.stars.add(ev.x, gy + 0.5, ev.z, ev.r * 0.8, 0.16, CORE, 1.6);
      for (let k = 0; k < 18; k++) { const a = Math.random() * Math.PI * 2, d = Math.random() * ev.r; fx.particles.emitDir(ev.x + Math.cos(a) * d, gy + 0.15, ev.z + Math.sin(a) * d, 0, 4 + Math.random() * 4, 0, k % 3 ? BODY : CORE, 0.2, 0.4, 2.6); }
      this.glow.flash(ev.x, ev.z, ev.r * 1.2, GLOW, 0.6, 0.3);
    } else if (ev.t === 'gripSlam') {
      this.slams++; this.slamSeq++;
      const gy = this.groundAt(ev.x, ev.z), r = ev.r, big = ev.evolved;
      fx.stars.add(ev.x, gy + 0.8, ev.z, 3, 0.14, 0xffffff, 2.6);
      fx.flashes.add(ev.x, gy + 0.4, ev.z, r * 0.25, 0.14, BODY, 0.8);
      fx.rings.add(ev.x, gy + 0.15, ev.z, ev.shockR * 1.05, 0.45, BODY, 2.8, 0.55); // pink shock racing out
      fx.rings.add(ev.x, gy + 0.1, ev.z, ev.shockR * 0.8, 0.7, DUSTC, 1.4, 1.4);  // the dust ring behind it
      for (let k = 0; k < (big ? 26 : 18); k++) { // dust bursting out of the crater
        const a = (k / (big ? 26 : 18)) * Math.PI * 2 + Math.random() * 0.3, sp = r * (2.2 + Math.random() * 1.5);
        fx.blast?.dust(ev.x + Math.cos(a) * r * 0.5, gy + 0.3, ev.z + Math.sin(a) * r * 0.5, Math.cos(a) * sp, 0.8 + Math.random() * 1.5, Math.sin(a) * sp, 0.9 + Math.random() * 0.5, 0.6, 1.6 + r * 0.25, Math.random() < 0.3 ? 1 : 0);
      }
      for (let k = 0; k < 8; k++) { const a = (k / 8) * Math.PI * 2 + Math.random() * 0.4; fx.blast?.chips(ev.x + Math.cos(a) * r * 0.6, ev.z + Math.sin(a) * r * 0.6, Math.cos(a), Math.sin(a), big ? 3 : 2); }
      for (let k = 0; k < 16; k++) { const a = Math.random() * Math.PI * 2, sp = 6 + Math.random() * 6; fx.particles.emitDir(ev.x, gy + 0.4, ev.z, Math.cos(a) * sp, 1 + Math.random() * 3, Math.sin(a) * sp, k % 2 ? BODY : CORE, 0.24, 0.32, 2.8); }
      const g = sim.grips.find((q) => q.id === ev.id); // just released: its caught list is still there to read
      if (g) for (let k = 0; k < g.n; k++) {
        const e = sim.enemies[g.ei[k]]; if (!e.alive || e.id !== g.eid[k]) continue;
        const ey = this.groundAt(e.x, e.z);
        fx.blast?.dust(e.x, ey + 0.2, e.z, 0, 0.6, 0, 0.7, 0.5, 1.3, 0);
        fx.particles.emit(e.x, ey + 0.2, e.z, 4, 3, DUSTC, 0.5, 0.45, 1, 4, 2, 0.9);
      }
      const c = this.crk.find((q) => !q.on) ?? this.crk[0];
      Object.assign(c, { on: true, x: ev.x, z: ev.z, y: gy, r: r * 1.6, t: 0, yaw: Math.random() * 6.283 });
      if (FLASH.dim >= 1) fx.lights.flash(ev.x, gy + 1, ev.z, BODY, 40, 0.3, ev.shockR * 2.5);
      fx.shake = Math.min(1, fx.shake + (big ? 0.75 : 0.55));
      this.hitstop = Math.max(this.hitstop, 0.05);
      this.glow.flash(ev.x, ev.z, ev.shockR, GLOW, 0.6, 0.35);
      this.burn(ev.x, ev.z, r * 0.7);
      const i = sim.grips.indexOf(g!); if (i >= 0) this.slamFlash[i] = 0.3;
    }
  }

  update(sim: Sim, dt: number, cam: THREE.Camera): void {
    const camQ = cam.quaternion, camP = cam.position, tm = performance.now() / 1000;
    this.seals.begin(); this.cracks.begin(); this.rings.begin(); this.auras.begin(); this.tethers.begin();
    let want = 0;
    for (let gi = 0; gi < sim.grips.length; gi++) {
      const g = sim.grips[gi], gy = this.groundAt(g.x, g.z);
      this.slamFlash[gi] = Math.max(0, this.slamFlash[gi] - dt);
      if (!g.alive) { const f = this.slamFlash[gi]; if (f > 0) this.seals.push(g.x, gy + 0.08, g.z, g.r * 2.4 * (1 + (0.3 - f) * 1.5), tm * 0.4, f / 0.3, 1, f / 0.3, g.evo ? 1 : 0); continue; }
      const t1 = GRIP_LIFT, t2 = t1 + g.hold, open = easeOut(clamp01(g.t / 0.18)), charge = clamp01((g.t - t1) / (t2 - t1 + GRIP_FALL));
      want = 1; this.aimYaw = Math.atan2(g.x - sim.player.x, g.z - sim.player.z);
      this.seals.push(g.x, gy + 0.08, g.z, g.r * 2.4 * (0.4 + 0.6 * open), tm * 0.4 * (1 + charge * 2), open, charge, Math.max(0, 1 - g.t / 0.15) * 0.8, g.evo ? 1 : 0);
      const falling = g.t >= t2;
      for (let k = 0; k < g.n; k++) {
        const e = sim.enemies[g.ei[k]]; if (!e.alive || e.id !== g.eid[k]) continue;
        const ey = this.groundAt(e.x, e.z), s = e.def.radius * e.scale, cy = ey + 0.55 + Math.min(1.4, s * 0.8) + e.gripY;
        const sz = s * 2.6 + 0.9, heat = falling ? 1 : 0.3 + 0.2 * Math.sin(tm * 8 + k);
        this.auras.push(e.x, cy, e.z, sz * 1.5, camQ, 0.8);
        pushRolled(this.rings, e.x, cy, e.z, sz, camQ, 0.9, heat, tm * (falling ? 9 : 2.2) + k * 1.3);
        // tether: from the seal under the foe up to it
        const sg = this.seg; sg[0] = e.x; sg[1] = ey + 0.08; sg[2] = e.z; sg[3] = e.x; sg[4] = cy - s * 0.3; sg[5] = e.z;
        this.tethers.strip(sg, 2, 0.05, 0.12, 0.1, 0.85 * (0.75 + 0.25 * Math.sin(tm * 14 + k)), col(_e, BODY, 1.2), col(_c, CORE, 1.5), 0.4, camP);
        if (!falling && g.t < t1 && Math.random() < 0.7) this.fx.particles.emitDir(e.x + (Math.random() - 0.5) * s, ey + 0.2, e.z + (Math.random() - 0.5) * s, 0, 5 + Math.random() * 3, 0, Math.random() < 0.5 ? BODY : CORE, 0.16, 0.35, 2.6);
        else if (!falling && Math.random() < 0.15) this.fx.particles.emitDir(e.x + (Math.random() - 0.5) * s, cy - s * 0.4, e.z + (Math.random() - 0.5) * s, 0, 0.6, 0, BODY, 0.12, 0.4, 2.4);
        if (falling) for (let q = 0; q < 2; q++) this.fx.particles.emitDir(e.x + (Math.random() - 0.5) * s, cy + 0.6 + Math.random() * 1.2, e.z + (Math.random() - 0.5) * s, 0, -22, 0, q ? CORE : BODY, 0.16, 0.1, 3); // speed lines
      }
    }
    this.pose += (want - this.pose) * Math.min(1, dt * (want ? 14 : 6));
    for (const c of this.crk) {
      if (!c.on) continue;
      c.t += dt; const a = c.t < 2 ? 1 : 1 - (c.t - 2) / 1.2; if (a <= 0) { c.on = false; continue; }
      this.cracks.push(c.x, this.groundAt(c.x, c.z) + 0.06, c.z, c.r * 2, c.yaw, a * clamp01(c.t / 0.06));
    }
    this.seals.end(); this.cracks.end(); this.rings.end(); this.auras.end(); this.tethers.end();
  }

  glowPools(g: GroundGlow, sim: Sim): void {
    for (const q of sim.grips) {
      if (!q.alive) continue;
      g.blob(q.x, this.groundAt(q.x, q.z) + 0.8, q.z, q.r * 1.1, GLOW, 0.7);
      for (let k = 0; k < q.n; k++) { const e = sim.enemies[q.ei[k]]; if (e.alive && e.id === q.eid[k]) g.blob(e.x, this.groundAt(e.x, e.z) + 0.8 + e.gripY, e.z, 1.2, GLOW, 0.35); }
    }
  }

  clear(): void { this.seals.clear(); this.cracks.clear(); this.rings.clear(); this.auras.clear(); this.tethers.clear(); for (const c of this.crk) c.on = false; this.slamFlash.fill(0); this.pose = 0; this.hitstop = 0; }
}
